import type { InboxWorkflowCommitResult, InboxWorkflowCommitRowResult } from "./types"

export type InboxWorkflowResultOutcomeStatus = "success" | "skipped" | "failed"
export type InboxWorkflowStatusKind = "open" | "review" | "warning" | "done"
export type InboxWorkflowStatusMode = "attention" | "all"

export type InboxWorkflowResultOutcome = {
  actionCaseId: string
  stepKey: string
  status: InboxWorkflowResultOutcomeStatus
  label: string
  message: string
  hint: string
}

export type InboxWorkflowResultOutcomeMap = Record<string, InboxWorkflowResultOutcome>

export type InboxWorkflowStatusPresentation = {
  kind: InboxWorkflowStatusKind
  label: string
  detail: string | null
  hint: string | null
  requiresAttention: boolean
}

type WorkflowStatusInput = {
  outcome?: InboxWorkflowResultOutcome | null
  compatible?: boolean
  open?: boolean
  resolved?: boolean
  stepStatus?: string | null
  error?: string | null
}

const cleanText = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const normalizedStatus = (value: string): InboxWorkflowResultOutcomeStatus | null => {
  const normalized = value.trim().toLowerCase()

  if (normalized === "success") return "success"

  if (normalized === "skipped") return "skipped"

  if (normalized === "failed") return "failed"

  return null
}

const labelForStatus = (status: InboxWorkflowResultOutcomeStatus) => {
  if (status === "failed") return "Prüfung nötig"

  if (status === "skipped") return "Warnung"

  return "Erledigt"
}

const defaultMessageForStatus = (status: InboxWorkflowResultOutcomeStatus) => {
  if (status === "failed") return "Diese Zeile konnte nicht abgeschlossen werden."

  if (status === "skipped") return "Diese Zeile wurde nicht verändert."

  return "Diese Zeile wurde gespeichert."
}

const defaultHintForStatus = (status: InboxWorkflowResultOutcomeStatus) => {
  if (status === "failed") {
    return "Die Zeile bleibt offen und kann nach Korrektur erneut ausgeführt werden."
  }

  if (status === "skipped") {
    return "Die Zeile wurde nicht verändert. Prüfen Sie sie bei Bedarf erneut."
  }

  return "Die erfolgreiche Änderung wurde gespeichert."
}

const outcomeFromRow = (
  row: InboxWorkflowCommitRowResult,
  stepKey: string,
): InboxWorkflowResultOutcome | null => {
  const actionCaseId = cleanText(row.action_case_id)
  const status = normalizedStatus(cleanText(row.status))

  if (!actionCaseId || !status) return null

  const message = cleanText(row.error, row.detail) || defaultMessageForStatus(status)

  return {
    actionCaseId,
    stepKey,
    status,
    label: labelForStatus(status),
    message,
    hint: defaultHintForStatus(status),
  }
}

export const buildInboxWorkflowRowOutcomeMapForStep = (
  results: InboxWorkflowCommitResult[],
  stepKey: string,
): InboxWorkflowResultOutcomeMap => {
  const rowsById: InboxWorkflowResultOutcomeMap = {}
  for (const result of results) {
    if (result.step_key !== stepKey) continue

    for (const row of result.rows) {
      const outcome = outcomeFromRow(row, result.step_key)

      if (outcome) rowsById[outcome.actionCaseId] = outcome
    }
  }

  return rowsById
}

const presentation = (
  kind: InboxWorkflowStatusKind,
  detail: string | null = null,
  hint: string | null = null,
): InboxWorkflowStatusPresentation => {
  if (kind === "review") {
    return {
      kind,
      label: "Prüfung nötig",
      detail,
      hint: hint || "Diese Zeile bleibt offen und kann nach Korrektur erneut ausgeführt werden.",
      requiresAttention: true,
    }
  }

  if (kind === "warning") {
    return {
      kind,
      label: "Warnung",
      detail,
      hint: hint || "Diese Zeile wurde nicht verändert. Prüfen Sie sie bei Bedarf erneut.",
      requiresAttention: true,
    }
  }

  if (kind === "done") {
    return {
      kind,
      label: "Erledigt",
      detail,
      hint: hint || "Diese Zeile ist abgeschlossen.",
      requiresAttention: false,
    }
  }

  return {
    kind,
    label: "Offen",
    detail,
    hint: hint || "Diese Zeile ist noch zu bearbeiten.",
    requiresAttention: false,
  }
}

export const inboxWorkflowStatusFromRowState = ({
  outcome = null,
  compatible = true,
  open = false,
  resolved = false,
  stepStatus = null,
  error = null,
}: WorkflowStatusInput): InboxWorkflowStatusPresentation => {
  if (outcome?.status === "failed") return presentation("review", outcome.message, outcome.hint)

  if (outcome?.status === "skipped") return presentation("warning", outcome.message, outcome.hint)

  if (outcome?.status === "success") return presentation("done", outcome.message, outcome.hint)

  if (!compatible) {
    return presentation(
      "review",
      cleanText(error) || "Diese Zeile passt nicht zu diesem Arbeitsschritt.",
    )
  }

  if (stepStatus === "skipped")
    return presentation("warning", "Dieser Schritt wurde für die Zeile übersprungen.")

  if (resolved) return presentation("done")

  if (open) return presentation("open")

  return presentation("done")
}

export const hasInboxWorkflowAttentionStatus = (row: {
  workflowStatus?: InboxWorkflowStatusPresentation | null
}) => Boolean(row.workflowStatus?.requiresAttention)

export const filterInboxWorkflowRowsByStatusMode = <
  Row extends {
    workflowStatus?: InboxWorkflowStatusPresentation | null
  },
>(
  rows: Row[],
  mode: InboxWorkflowStatusMode,
): Row[] => (mode === "attention" ? rows.filter(hasInboxWorkflowAttentionStatus) : rows)

export const hasInboxWorkflowProblemOutcome = (rowOutcomes: InboxWorkflowResultOutcomeMap) =>
  Object.values(rowOutcomes).some((outcome) => outcome.status !== "success")

export const defaultInboxWorkflowStatusMode = (
  hasAttentionRows: boolean,
  rowOutcomes: InboxWorkflowResultOutcomeMap,
): InboxWorkflowStatusMode =>
  hasAttentionRows && hasInboxWorkflowProblemOutcome(rowOutcomes) ? "attention" : "all"

export const resolveInboxWorkflowStatusMode = ({
  hasAttentionRows,
  requestedMode,
  rowOutcomes,
}: {
  hasAttentionRows: boolean
  requestedMode?: InboxWorkflowStatusMode | null
  rowOutcomes: InboxWorkflowResultOutcomeMap
}): InboxWorkflowStatusMode => {
  if (!hasAttentionRows) return "all"

  return requestedMode ?? defaultInboxWorkflowStatusMode(hasAttentionRows, rowOutcomes)
}

export const shouldShowInboxWorkflowStatusModeToggle = <
  Row extends {
    workflowStatus?: InboxWorkflowStatusPresentation | null
  },
>(
  rows: Row[],
) => rows.some(hasInboxWorkflowAttentionStatus)
